import { X } from "@phosphor-icons/react";
import {
  Close,
  Content,
  Overlay,
  Portal,
  Root,
  Trigger,
} from "@radix-ui/react-dialog";
import { clsx } from "clsx";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { cn, getWhitelabelCustomization } from "common/helpers/utils";
import MobileProfilePopup from "common/layouts/WorkspaceLayout/MobileLayout/MobileProfilePopup";
import type { NavItemType } from "common/types";

import NavItem from "../NavItem";

import AimfoxLogoOrange from "assets/aimfoxLogos/aimfox-logo-orange.svg?react";
import HamburgerIcon from "assets/icons/hamburger.svg?react";

export default function MobileNav({ navItems }: { navItems: NavItemType[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { whitelabelCustomization } = getWhitelabelCustomization();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <Root open={isOpen} onOpenChange={setIsOpen}>
      <Trigger asChild>
        <Button size="lg" variant="outline-black" intent="iconOnly">
          <HamburgerIcon />
        </Button>
      </Trigger>
      <Portal>
        <Overlay
          className={cn(
            "fixed inset-0 z-overlay bg-black-950/60 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <Content
          className={clsx(
            "flex flex-col fixed z-dialog overflow-y-auto inset-y-0 px-2 pb-2 left-0 w-[375px] scrollbar-thin bg-[linear-gradient(132deg,#FFF_-4.58%,#F5F6F9_42.09%,#DCE1EA_85.29%)]",
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left data-[state=closed]:duration-300 data-[state=open]:duration-300 transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out",
          )}
        >
          <div className="flex justify-between items-center h-20 px-2">
            {whitelabelCustomization ? (
              <img
                src={whitelabelCustomization.logo}
                alt=""
                className="max-h-10"
              />
            ) : (
              <div className="flex items-center gap-x-1">
                <AimfoxLogoOrange className="w-10" />
                <span className="text-2xl-bold">Aimfox</span>
              </div>
            )}
            <Close asChild>
              <Button size="lg" variant="ghost-black" intent="iconOnly">
                <X />
              </Button>
            </Close>
          </div>
          <div className="overflow-y-auto scrollbar-thin gap-y-2 flex flex-col p-2.5 -m-2.5">
            {navItems.map(({ path, label, icon, notifications }) => (
              <NavLink to={path} key={path}>
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={icon}
                    isOnboarding={path === "onboarding"}
                    isExpanded
                  >
                    {label}
                    {!!notifications && (
                      <Tag className="ml-auto" variant="primary">
                        {notifications}
                      </Tag>
                    )}
                  </NavItem>
                )}
              </NavLink>
            ))}
          </div>
          <MobileProfilePopup />
        </Content>
      </Portal>
    </Root>
  );
}
